
'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { KbvPluginService } from '@/services/kbvPluginService';
import { BANG_KEO_PAGE_LIMIT } from '@/contants';

export default function useBangKeoMatches(queryObject = {}, useLoadMore = true) {
  const [hotLeaguesWithMatches, setHotLeaguesWithMatches] = useState([]);
  const [hotPage, setHotPage] = useState(1);
  const [hotTotal, setHotTotal] = useState(0);
  const [leaguesWithMatches, setLeaguesWithMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(queryObject.page || 1);
  const [limit] = useState(queryObject.limit || BANG_KEO_PAGE_LIMIT);
  const [total, setTotal] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);
  const isLoadingMoreRef = useRef(false);

  const hasMore = page * limit < total;
  const hasMoreHot = hotPage * BANG_KEO_PAGE_LIMIT < hotTotal;

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        setLoading(true);
        setError(null);
        setTotal(0);
        const result = await KbvPluginService.fetchBangKeoMatches(queryObject);
        setLeaguesWithMatches(result?.data?.data || []);

        result?.data?.total && setTotal(result?.data?.total);
        result?.data?.page && setPage(result?.data?.page);

      } catch (err) {
        setTotal(0);
        setError(err);
        setLeaguesWithMatches([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [JSON.stringify(queryObject)]);

  const hotQuery = {
    include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
    filterInclude: "date:all",
    filterOdds: -1,
    cache: false,
    limit: BANG_KEO_PAGE_LIMIT,
  };

  useEffect(() => {
    const fetchHotLeagues = async () => {
      const result = await KbvPluginService.fetchBangKeoMatches({
        ...hotQuery,
        page: 1,
      });
      const data = result?.data?.data || [];
      setHotLeaguesWithMatches(data);
      setHotTotal(result?.data?.total ?? 0);
      setHotPage(result?.data?.page ?? 1);
    };
    fetchHotLeagues();
  }, []);

  const handleLoadMoreHot = useCallback(async () => {
    if (isLoadingMoreRef.current || !hasMoreHot) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    try {
      const nextPage = hotPage + 1;
      const result = await KbvPluginService.fetchBangKeoMatches({
        ...hotQuery,
        page: nextPage,
      });
      const newItems = result?.data?.data || [];
      if (newItems.length) {
        setHotLeaguesWithMatches((prev) => [...prev, ...newItems]);
        setHotPage(nextPage);
      }
    } catch (err) {
      console.error("Load more hot error:", err);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [hotPage, hasMoreHot]);



  const handleLoadMore = useCallback(async () => {
      if (isLoadingMoreRef.current || !hasMore) return;

      isLoadingMoreRef.current = true;
      setIsLoadingMore(true); // Set state to trigger re-render
      try {
        const nextPage = page + 1;
        const result = await KbvPluginService.fetchBangKeoMatches({
          ...queryObject,
          page: nextPage,
          limit,
        });

        let newItems = result?.data?.data || [];

        if (newItems.length) {
          setLeaguesWithMatches((prev) => [...prev, ...newItems]);
          setPage(nextPage);
        }
      } catch (err) {
        console.error('Load more error:', err);
      } finally {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false); // Reset state
      }
    }, [page, limit, queryObject, hasMore]);

  useEffect(() => {
    if (!useLoadMore || !hasMore || !loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMoreRef.current) {
          handleLoadMore();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '30%',
      }
    );

    observer.observe(loadMoreRef.current);
    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [useLoadMore, hasMore, handleLoadMore]);

  return {
    hotLeaguesWithMatches,
    leaguesWithMatches,
    loading,
    error,
    total,
    hotTotal,
    hasMore,
    hasMoreHot,
    loadMoreRef,
    isLoadingMoreRef,
    isLoadingMore,
    handleLoadMore,
    handleLoadMoreHot,
    matches: hotLeaguesWithMatches.flatMap(({ matches }) => matches),
  };
}